import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { categoryApi } from "@/lib/api";

interface IncompleteSetupAlertProps {
  onDismiss?: () => void;
  onOpenSetup?: () => void;
}

export function IncompleteSetupAlert({ onDismiss, onOpenSetup }: IncompleteSetupAlertProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hasCategories, setHasCategories] = useState<boolean | null>(null);

  useEffect(() => {
    const checkCategories = async () => {
      const skippedWithoutCategories = localStorage.getItem('onboarding_skipped_without_categories');
      
      if (skippedWithoutCategories === 'true') {
        // Double check if user has since added categories
        try {
          const categories = await categoryApi.getAll();
          if (categories.length > 0) {
            // User has added categories, remove the flag
            localStorage.removeItem('onboarding_skipped_without_categories');
            setHasCategories(true);
            setIsVisible(false);
          } else {
            setHasCategories(false);
            setIsVisible(true);
          }
        } catch (error) {
          console.error('Failed to check categories:', error);
          setIsVisible(true);
          setHasCategories(false);
        }
      }
    };

    checkCategories();
  }, []);

  const handleSetupCategories = () => {
    if (onOpenSetup) {
      onOpenSetup();
    } else {
      navigate('/categories');
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isVisible || hasCategories) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-amber-500 text-white shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm sm:text-base truncate">
                {t('incompleteSetup.title', 'Setup Incomplete')}
              </p>
              <p className="text-xs sm:text-sm text-amber-100 hidden sm:block">
                {t('incompleteSetup.description', 'Add categories to start tracking your expenses')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white text-amber-700 hover:bg-amber-50 text-xs sm:text-sm h-8"
              onClick={handleSetupCategories}
            >
              <Tag className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              <span className="hidden sm:inline">{t('incompleteSetup.addCategories', 'Add Categories')}</span>
              <span className="sm:hidden">{t('incompleteSetup.setup', 'Setup')}</span>
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-white hover:bg-amber-600"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to check if setup is incomplete
export function useIncompleteSetup() {
  const [hasCategories, setHasCategories] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const categories = await categoryApi.getAll();
        setHasCategories(categories.length > 0);
        
        // Clear the flag if user has categories
        if (categories.length > 0) {
          localStorage.removeItem('onboarding_skipped_without_categories');
        }
      } catch (error) {
        console.error('Failed to check setup:', error);
        setHasCategories(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSetup();
  }, []);

  return {
    hasCategories,
    isLoading,
    isSetupIncomplete: hasCategories === false
  };
}

